import {
  MDBBtn, MDBCard, MDBCardBody, MDBCol, MDBRow, MDBTable,
  MDBTableBody, MDBTableHead, MDBBtnGroup, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalTitle,
  MDBSelect, MDBInput,
  MDBPagination, MDBPaginationItem, MDBPaginationLink,
  MDBDropdown, MDBDropdownToggle, MDBDropdownMenu, MDBDropdownItem,
  MDBCardTitle
} from "mdb-react-ui-kit"
import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { generateClient } from "aws-amplify/api"
import { isoToGermanDate } from "../../utils/dateTools"
import { deleteTicket } from "../../graphql/mutations"
import ShowTicket from "../../components-user/tickets/ShowTicket"
import getEventDays from '../../utils/getEventDays';
import { getLocalizedText } from "../../utils/localizedText"
import { captureError } from '../../utils/sentryHelpers';
import { useIndustry } from '../../utils/industryContextHook';
import TicketChart from './ticket-chart';
import ChartSkeleton from './ticket-chart/components/ChartSkeleton';
import ErrorFallback from '../../components/toolbox/ErrorFallback';

const client = generateClient()
const LOUNGES_2026_ID = "cb7a916e-ed71-483a-8177-22532ab0b237";

export default function AdminAllTickets() {
  const { t } = useTranslation()
  const { currentIndustry, availableIndustries } = useIndustry()

  const [isLoading, setIsLoading] = useState(true)
  const [isStatsLoading, setIsStatsLoading] = useState(true)
  const [isCompareLoading, setIsCompareLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  // Table data (current page only, from OpenSearch)
  const [tickets, setTickets] = useState([])
  const [totalTickets, setTotalTickets] = useState(0)

  // Stats/Chart data (all tickets, lightweight fields only)
  const [statsTickets, setStatsTickets] = useState([])

  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")

  const [allEvents, setAllEvents] = useState([]);
  const [selectedIndustryId, setSelectedIndustryId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(LOUNGES_2026_ID);
  const [selectedDays, setSelectedDays] = useState([]);
  const [compareEventId, setCompareEventId] = useState(null);
  const [compareChartTickets, setCompareChartTickets] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  // Set default industry from currentIndustry
  useEffect(() => {
    if (currentIndustry?.id && !selectedIndustryId) {
      setSelectedIndustryId(currentIndustry.id);
    }
  }, [currentIndustry?.id]);

  // Filter events by selected industry and sort: future first (ascending), then past (descending)
  const events = useMemo(() => {
    const now = new Date();
    return allEvents
      .filter(event => {
        if (!selectedIndustryId) return true;
        return event.industries?.items?.some(
          item => item.industry?.id === selectedIndustryId
        );
      })
      .sort((a, b) => {
        const aDate = new Date(a.startDate);
        const bDate = new Date(b.startDate);
        const aFuture = aDate >= now;
        const bFuture = bDate >= now;
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        if (aFuture && bFuture) return aDate - bDate;
        return bDate - aDate;
      });
  }, [allEvents, selectedIndustryId]);

  // Auto-select first event when filtered events change
  useEffect(() => {
    if (events.length && !events.find(e => e.id === selectedEventId)) {
      setSelectedEventId(events[0].id);
      setSelectedDays([]);
    }
  }, [events]);

  // Debounce search term
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Base filter for event + days (OpenSearch operators)
  const buildEventFilter = useCallback(() => {
    const filters = [];

    if (selectedEventId) {
      filters.push({ ticketEventId: { eq: selectedEventId } });
    } else {
      filters.push({ ticketEventId: { eq: LOUNGES_2026_ID } });
    }

    if (selectedDays.length === 1) {
      filters.push({ eventDays: { eq: selectedDays[0] } });
    } else if (selectedDays.length > 1) {
      filters.push({ or: selectedDays.map(day => ({ eventDays: { eq: day } })) });
    }

    return filters.length === 1 ? filters[0] : { and: filters };
  }, [selectedEventId, selectedDays]);

  // Table filter (event + days + search)
  const buildTableFilter = useCallback(() => {
    const baseFilter = buildEventFilter();

    if (!debouncedSearchTerm.trim()) return baseFilter;

    const term = debouncedSearchTerm.toLowerCase();
    const searchFilter = {
      or: [
        { firstName: { wildcard: `*${term}*` } },
        { lastName: { wildcard: `*${term}*` } },
        { companyName: { wildcard: `*${term}*` } },
        { billingEmail: { wildcard: `*${term}*` } },
        { invitationCode: { wildcard: `*${term}*` } },
        { orderNumber: { wildcard: `*${term}*` } },
        { visitorType: { wildcard: `*${term}*` } },
      ]
    };

    if (baseFilter.and) {
      return { and: [...baseFilter.and, searchFilter] };
    }
    return { and: [baseFilter, searchFilter] };
  }, [buildEventFilter, debouncedSearchTerm]);

  // Compare filter (OpenSearch, for compare chart only)
  const buildCompareFilter = useCallback(() => {
    return { ticketEventId: { eq: compareEventId } };
  }, [compareEventId]);

  const getEvents = useCallback(async () => {
    try {
      const res = await client.graphql({
        query: /* GraphQL */ `
                query ListEvents {
                    listEvents {
                        items {
                            id
                            name {
                                text
                                language
                            }
                            startDate
                            endDate
                            ticketCancellationDeadline
                            status
                            industries {
                                items {
                                    industry {
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
                `
      });
      const items = res.data.listEvents.items.filter(Boolean);
      setAllEvents(items);
      return items;
    } catch (error) {
      console.log("error on fetching events", error);
      setLoadError(true);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'fetch-events',
        consoleMessage: 'error on fetching events',
        context: {}
      });
    }
  }, []);

  // Fetch tickets for table (paginated via OpenSearch)
  const getTickets = useCallback(async () => {
    setIsLoading(true);
    const filter = buildTableFilter();
    try {
      const { data } = await client.graphql({
        query: /* GraphQL */ `
            query SearchTickets(
              $filter: SearchableTicketFilterInput
              $sort: [SearchableTicketSortInput]
              $limit: Int
              $from: Int
            ) {
              searchTickets(filter: $filter, sort: $sort, limit: $limit, from: $from) {
                items {
                  id
                  createdByUserId
                  eventDays
                  status
                  paymentStatus
                  type
                  visitorType
                  addons
                  ticketFile {
                    url
                    s3Path
                    fileName
                    type
                    name
                  }
                  notes
                  personalAddress {
                    firstName
                    lastName
                    title
                    salutation
                    street
                    street2
                    streetNumber
                    company
                    contactPerson
                    city
                    zip
                    countryCode
                  }
                  billingAddress {
                    firstName
                    lastName
                    title
                    salutation
                    street
                    street2
                    streetNumber
                    company
                    contactPerson
                    city
                    zip
                    countryCode
                  }
                  invitationCode
                  orderNumber
                  billingEmail
                  createdAt
                  updatedAt
                  userTicketsId
                  ticketEventId
                  ticketCompanyId
                  ticketPaymentId
                }
                total
              }
            }
            `,
        variables: {
          filter,
          sort: [{ field: "createdAt", direction: "desc" }],
          limit: rowsPerPage,
          from: (page - 1) * rowsPerPage,
        },
      });
      setTickets(data.searchTickets.items.filter(Boolean));
      setTotalTickets(data.searchTickets.total);
    } catch (error) {
      console.error(error);
      setLoadError(true);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'fetch-tickets',
        consoleMessage: 'error',
        context: { selectedEventId }
      });
    } finally {
      setIsLoading(false);
    }
  }, [buildTableFilter, page, rowsPerPage, selectedEventId]);

  // Lightweight fetch for stats + chart (all tickets for the event, minimal fields)
  const getStatsData = useCallback(async () => {
    setIsStatsLoading(true);
    let items = [];
    let nextToken = null;
    const filter = buildEventFilter();
    try {
      do {
        const { data } = await client.graphql({
          query: /* GraphQL */ `
              query SearchTickets(
                $filter: SearchableTicketFilterInput
                $limit: Int
                $nextToken: String
              ) {
                searchTickets(filter: $filter, limit: $limit, nextToken: $nextToken) {
                  items {
                    id
                    createdAt
                    eventDays
                    visitorType
                  }
                  nextToken
                }
              }
              `,
          variables: { filter, limit: 1000, nextToken },
        });
        items = items.concat(data.searchTickets.items.filter(Boolean));
        nextToken = data.searchTickets.nextToken;
      } while (nextToken);
      setStatsTickets(items);
    } catch (error) {
      console.error(error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'fetch-stats-data',
        consoleMessage: 'error',
        context: { selectedEventId }
      });
    } finally {
      setIsStatsLoading(false);
    }
  }, [buildEventFilter, selectedEventId]);

  // Lightweight fetch for comparison event chart data
  const getCompareChartData = useCallback(async () => {
    if (!compareEventId) {
      setCompareChartTickets([]);
      return;
    }
    setIsCompareLoading(true);
    let items = [];
    let nextToken = null;
    const filter = buildCompareFilter();
    try {
      do {
        const { data } = await client.graphql({
          query: /* GraphQL */ `
              query SearchTickets(
                $filter: SearchableTicketFilterInput
                $limit: Int
                $nextToken: String
              ) {
                searchTickets(filter: $filter, limit: $limit, nextToken: $nextToken) {
                  items {
                    id
                    createdAt
                    eventDays
                    visitorType
                  }
                  nextToken
                }
              }
              `,
          variables: { limit: 1000, nextToken, filter },
        });
        items = items.concat(data.searchTickets.items.filter(Boolean));
        nextToken = data.searchTickets.nextToken;
      } while (nextToken);
      setCompareChartTickets(items);
    } catch (error) {
      console.error(error);
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'fetch-compare-chart-data',
        consoleMessage: 'error',
        context: { compareEventId }
      });
    } finally {
      setIsCompareLoading(false);
    }
  }, [compareEventId]);

  useEffect(() => {
    getEvents()
  }, [getEvents])

  useEffect(() => {
    getTickets();
  }, [getTickets])

  useEffect(() => {
    getStatsData();
  }, [getStatsData])

  useEffect(() => {
    getCompareChartData();
  }, [getCompareChartData])

  // Live subscription: receive new tickets in real-time
  const selectedEventIdRef = useRef(selectedEventId);
  selectedEventIdRef.current = selectedEventId;

  useEffect(() => {
    if (!selectedEventId) return;

    const subscription = client.graphql({
      query: /* GraphQL */ `
        subscription OnCreateTicket($filter: ModelSubscriptionTicketFilterInput) {
          onCreateTicket(filter: $filter) {
            id
            createdAt
            eventDays
            visitorType
            ticketEventId
          }
        }
      `,
      variables: {
        filter: { ticketEventId: { eq: selectedEventId } }
      }
    }).subscribe({
      next: async ({ data }) => {
        const subTicket = data.onCreateTicket;
        if (!subTicket || subTicket.ticketEventId !== selectedEventIdRef.current) return;

        // Update stats/chart data immediately (these fields are always available)
        setStatsTickets(prev => [...prev, {
          id: subTicket.id,
          createdAt: subTicket.createdAt,
          eventDays: subTicket.eventDays,
          visitorType: subTicket.visitorType,
        }]);
        setTotalTickets(prev => prev + 1);

        // Fetch full ticket data via query (subscription omits auth-protected fields)
        try {
          const { data: queryData } = await client.graphql({
            query: /* GraphQL */ `
              query GetTicket($id: ID!) {
                getTicket(id: $id) {
                  id
                  createdByUserId
                  eventDays
                  status
                  paymentStatus
                  type
                  visitorType
                  addons
                  ticketFile { url s3Path fileName type name }
                  notes
                  personalAddress {
                    firstName lastName title salutation
                    street street2 streetNumber company
                    contactPerson city zip countryCode
                  }
                  billingAddress {
                    firstName lastName title salutation
                    street street2 streetNumber company
                    contactPerson city zip countryCode
                  }
                  invitationCode
                  orderNumber
                  billingEmail
                  createdAt
                  updatedAt
                  userTicketsId
                  ticketEventId
                  ticketCompanyId
                  ticketPaymentId
                }
              }
            `,
            variables: { id: subTicket.id }
          });
          const fullTicket = queryData.getTicket;
          if (fullTicket) {
            setTickets(prev => [fullTicket, ...prev]);
          }
        } catch (err) {
          // Fallback: use subscription data as-is
          setTickets(prev => [subTicket, ...prev]);
        }
      },
      error: (error) => {
        console.warn("Ticket subscription error:", error);
        captureError(error, {
          feature: 'event-management-admin',
          operation: 'ticket-subscription',
          context: { selectedEventId }
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [selectedEventId]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedEventId, selectedDays]);

  const selectedEvent = useMemo(
    () => events.find(e => e.id === selectedEventId),
    [events, selectedEventId]
  );

  const compareEvent = useMemo(
    () => events.find(e => e.id === compareEventId),
    [events, compareEventId]
  );

  const eventDaysList = useMemo(
    () => selectedEvent ? getEventDays(selectedEvent) : [],
    [selectedEvent]
  );

  const compareEventDaysList = useMemo(
    () => compareEvent ? getEventDays(compareEvent) : [],
    [compareEvent]
  );

  const toggleDay = useCallback((day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  }, []);

  const totalPages = Math.ceil(totalTickets / rowsPerPage);

  // Sliding window: show 5 pages centered around current page
  const paginationPages = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = start + windowSize - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - windowSize + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const handleDeleteTicket = useCallback(async (id) => {
    try {
      const data = await client.graphql({
        query: deleteTicket,
        variables: { input: { id } },
      })
      console.log(data)
      setTickets(prev => prev.filter(ticket => ticket.id !== id))
      setTotalTickets(prev => prev - 1)
      setStatsTickets(prev => prev.filter(ticket => ticket.id !== id))
    }
    catch (error) {
      console.error(error)
      captureError(error, {
        feature: 'event-management-admin',
        operation: 'delete-ticket',
        consoleMessage: 'error',
        context: { ticketId: id }
      });
    }
  }, []);

  const [viewTicketModal, setViewTicketModal] = useState(false);
  const toggleViewTicketModal = () => setViewTicketModal(!viewTicketModal);

  const [selectedTicket, setSelectedTicket] = useState(null);

  if (loadError) return <ErrorFallback />;

  return (
    <>

      <MDBCard>
        <MDBCardBody>
          <MDBCardTitle tag="h2">
            {t("Tickets")}
          </MDBCardTitle>
          <MDBRow className="mb-2">
            <MDBCol size="12" md="6" xl="4" className="my-2">
              <MDBSelect label={t("Industry")}
                className="select-wide-dropdown"
                onChange={e => { if (e?.value !== undefined) setSelectedIndustryId(e.value); }}
                value={selectedIndustryId}
                data={
                  [
                    { text: t("All"), value: null },
                    ...availableIndustries.map(industry => ({ text: getLocalizedText(industry.name), value: industry.id }))
                  ]
                }
              />
            </MDBCol>
          </MDBRow>
          <MDBRow className="mb-2">
            <MDBCol size="12" md="6" xl="4" className="my-2">
              <MDBSelect label={t("Event")}
                className="select-wide-dropdown"
                onChange={e => { if (e?.value !== undefined) { setSelectedEventId(e.value); setSelectedDays([]); } }}
                value={selectedEventId}
                data={
                  events.map(event => ({ text: `${getLocalizedText(event.name)}: ${event.startDate && isoToGermanDate(event.startDate)} - ${event.endDate && isoToGermanDate(event.endDate)}`, value: event.id }))
                }
              />
            </MDBCol>
            <MDBCol size="12" md="6" xl="4" className="my-2">
              <MDBSelect label={t("Compare with")}
                className="select-wide-dropdown"
                onChange={e => { if (e?.value !== undefined) setCompareEventId(e.value); }}
                value={compareEventId}
                data={
                  [
                    { text: t("None"), value: null },
                    ...events.map(event => ({ text: `${getLocalizedText(event.name)}: ${event.startDate && isoToGermanDate(event.startDate)} - ${event.endDate && isoToGermanDate(event.endDate)}`, value: event.id }))
                  ]
                }
              />
            </MDBCol>
            <MDBCol size="12" md="6" xl="4" className="my-2">
              <MDBInput
                label={t("Search tickets")}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t("Name, Company, Email, Invitation Code...")}
              />
            </MDBCol>
            <MDBCol size="12" md="auto" className="d-flex align-items-center">
              <MDBBtn color="light" onClick={() => { getTickets(); getStatsData(); getCompareChartData(); }} disabled={isLoading}>
                {isLoading ? <span className="spinner-border spinner-border-sm me-1" role="status" /> : "↻"} {t("Refresh")}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
          {selectedEventId && eventDaysList.length > 0 && (
            <div className="mb-3">
              <MDBBtnGroup size="sm">
                <MDBBtn
                  color={selectedDays.length === 0 ? "primary" : "light"}
                  onClick={() => setSelectedDays([])}
                >
                  {t("All days")}
                </MDBBtn>
                {eventDaysList.map(day => (
                  <MDBBtn
                    key={day}
                    color={selectedDays.includes(day) ? "primary" : "light"}
                    onClick={() => toggleDay(day)}
                  >
                    {isoToGermanDate(day)}
                  </MDBBtn>
                ))}
              </MDBBtnGroup>
            </div>
          )}
          {isStatsLoading ? (
            <ChartSkeleton />
          ) : (
            <TicketChart
              tickets={statsTickets}
              compareTickets={compareChartTickets}
              eventName={selectedEvent ? getLocalizedText(selectedEvent.name) : t("All")}
              compareEventName={compareEvent ? getLocalizedText(compareEvent.name) : ""}
              eventDays={eventDaysList}
              isCompareLoading={isCompareLoading}
              eventStartDate={selectedEvent?.startDate}
              eventEndDate={selectedEvent?.endDate}
              compareEventStartDate={compareEvent?.startDate}
              compareEventEndDate={compareEvent?.endDate}
            />
          )}
          <MDBRow className="mb-3">
            <MDBCol size="12" md={compareEventId ? "6" : "12"}>
              {selectedEvent && <h6 className="fw-bold">{getLocalizedText(selectedEvent.name)}</h6>}
              {isStatsLoading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <span className="text-muted">{t("Loading...")}</span>
                </div>
              ) : (
                <>
                  <h5>{t("Total tickets")}: {statsTickets.length}</h5>
                  <span>{t("Staff")}: {statsTickets.filter(ticket => ticket.visitorType === "staff").length}</span><br />
                  <span>{t("Visitors")}: {statsTickets.filter(ticket => ticket.visitorType !== "staff").length}</span>
                  {selectedEventId &&
                    <>
                      <hr className="my-2" />
                      {eventDaysList.map((day, index) => (
                        <span key={index}>{isoToGermanDate(day)}: {statsTickets.filter(ticket => ticket.eventDays.includes(day)).length} {t("Visitors")}<br /></span>
                      ))}
                      <hr className="my-2" />
                      <span>
                        {t("Total visits")}: {statsTickets.reduce((acc, ticket) => acc + ticket.eventDays.length, 0)}
                      </span>
                    </>
                  }
                </>
              )}
            </MDBCol>
            {compareEventId && compareEvent && (
              <MDBCol size="12" md="6" className="border-start">
                <h6 className="fw-bold">{getLocalizedText(compareEvent.name)}</h6>
                {isCompareLoading ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted">{t("Loading...")}</span>
                  </div>
                ) : (
                  <>
                    <h5>{t("Total tickets")}: {compareChartTickets.length}</h5>
                    <span>{t("Staff")}: {compareChartTickets.filter(ticket => ticket.visitorType === "staff").length}</span><br />
                    <span>{t("Visitors")}: {compareChartTickets.filter(ticket => ticket.visitorType !== "staff").length}</span>
                    <hr className="my-2" />
                    {compareEventDaysList.map((day, index) => (
                      <span key={index}>{isoToGermanDate(day)}: {compareChartTickets.filter(ticket => ticket.eventDays?.includes(day)).length} {t("Visitors")}<br /></span>
                    ))}
                    <hr className="my-2" />
                    <span>
                      {t("Total visits")}: {compareChartTickets.reduce((acc, ticket) => acc + (ticket.eventDays?.length || 0), 0)}
                    </span>
                  </>
                )}
              </MDBCol>
            )}
          </MDBRow>

          <MDBTable>
            <MDBTableHead>
              <tr>
                <th>#</th>
                <th>{t("User")}</th>
                <th>{t("Details")}</th>
                <th>{t("Booking date")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </MDBTableHead>
            <MDBTableBody>
              {isLoading && <tr><td colSpan="5" className="text-center"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></td></tr>}
              {tickets?.map((ticket, index) => (
                <tr key={ticket.id}>
                  <td>{totalTickets - ((page - 1) * rowsPerPage + index)}</td>
                  <td>
                    {ticket.personalAddress?.firstName} {ticket.personalAddress?.lastName}
                    <br />
                    {ticket.personalAddress?.company}
                  </td>

                  <td>
                    {ticket.eventDays.map((day, i) => (i !== 0 ? ", " : "") + isoToGermanDate(day))}
                    <br />
                    {ticket.visitorType}
                    <br />
                    {ticket.invitationCode}
                  </td>
                  <td>{isoToGermanDate(ticket.createdAt)}{" "}{new Date(ticket.createdAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</td>

                  <td>
                    <MDBBtnGroup>
                      <MDBBtn color="primary" size="sm"
                        onClick={() => {
                          setSelectedTicket(ticket)
                          toggleViewTicketModal()
                        }}
                      >{t("View")}</MDBBtn>
                    </MDBBtnGroup>
                  </td>
                </tr>
              ))
              }
            </MDBTableBody>
          </MDBTable>

          {totalPages > 1 && (
            <nav aria-label="Page navigation" className="d-flex mt-3">
              <MDBPagination className="mx-auto">
                <MDBPaginationItem>
                  {page !== 1 && <MDBPaginationLink className="text-dark" onClick={() => setPage(page - 1)}>{t("Previous")}</MDBPaginationLink>}
                </MDBPaginationItem>
                {paginationPages.map(p => (
                  <MDBPaginationItem key={p} active={p === page}>
                    <MDBPaginationLink className="text-dark" onClick={() => setPage(p)}>{p}</MDBPaginationLink>
                  </MDBPaginationItem>
                ))}
                {totalPages > 5 && (
                  <MDBDropdown dropup>
                    <MDBDropdownToggle color="white" className="text-dark">
                      ...
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="overflow-auto" style={{ maxHeight: "200px" }}>
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <MDBDropdownItem link key={i} active={i + 1 === page} className="text-dark" onClick={() => setPage(i + 1)}>
                          {i + 1}
                        </MDBDropdownItem>
                      ))}
                    </MDBDropdownMenu>
                  </MDBDropdown>
                )}
                <MDBPaginationItem>
                  {page !== totalPages && <MDBPaginationLink className="text-dark" onClick={() => setPage(page + 1)}>{t("Next")}</MDBPaginationLink>}
                </MDBPaginationItem>
              </MDBPagination>
              <MDBDropdown>
                <MDBDropdownToggle color="white" className="text-dark">
                  {rowsPerPage}
                </MDBDropdownToggle>
                <MDBDropdownMenu>
                  {[10, 15, 20, 50, 100].map(n => (
                    <MDBDropdownItem link key={n} onClick={() => { setRowsPerPage(n); setPage(1); }} className="text-dark">
                      {n}
                    </MDBDropdownItem>
                  ))}
                </MDBDropdownMenu>
              </MDBDropdown>
            </nav>
          )}
        </MDBCardBody>
      </MDBCard>

      <MDBModal open={viewTicketModal} onClose={() => setViewTicketModal(false)} position="top">
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Your ticket")}</MDBModalTitle>
              <MDBBtn className='btn-close' color='none' onClick={toggleViewTicketModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <ShowTicket ticketId={selectedTicket?.id} handleDeleteTicket={handleDeleteTicket} />

            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

    </>
  )
}